//changed due to package upgrade
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  alpha,
  Tooltip,
} from '@mui/material';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { AiOutlineEdit } from 'react-icons/ai';
import jwtAxios from '@projectK/services/auth/JWT';
import { Formik } from 'formik';
import * as yup from 'yup';
import AppGridContainer from '@projectK/components/AppGridContainer';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import AppInfoView from '@projectK/components/AppInfoView';
import AppModalConfirmation from '@projectK/components/AppModalConfirmation';
import { CloseRounded } from '@mui/icons-material';
import AppTextField from '@projectK/components/AppTextField';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IntlMessages from '@projectK/helpers/IntlMessages';

const formatRoleName = (roleName) => {
  if (!roleName) return '';
  return roleName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

const validationSchema = yup.object().shape({
  alias_name: yup
    .string()
    .matches(/^[^\d]*$/, <IntlMessages id='validation.aliasNameNoNumbers'/>)
    .max(100, <IntlMessages id='validation.aliasNameTooLong'/>)
    .nullable(),
});

const requestRoleValidationSchema = yup.object().shape({
  roleName: yup
    .string()
    .required(<IntlMessages id='validation.roleNameRequired'/>),
  roleDescription: yup
    .string()
    .required(<IntlMessages id='validation.roleDescriptionRequired'/>),
});

export default function RoleManagementPage({
  roles,
  rolePermissionCounts,
  onPermissionClick,
  fetchData,
}) {
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [isRequestRoleDialogOpen, setIsRequestRoleDialogOpen] = useState(false);

  const openRequestRoleDialog = (role = null) => {
    setIsRequestRoleDialogOpen(true);
  };

  const openDialog = (role = null) => {
    setSelectedRole(role);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const handlePermissionChipClick = (e, roleId) => {
    e.stopPropagation();
    if (onPermissionClick) {
      onPermissionClick(roleId);
    }
  };

  const handleCloseModalDialog = () => {
    setIsDialogOpen(false);
    setOpenConfirmationDialog();
    setIsRequestRoleDialogOpen(false);
  };

  const handleCloseDialog = () => {
    setOpenConfirmationDialog(false);
  };

  const handleDialogOpen = () => {
    setOpenConfirmationDialog(true);
  };

  const closeRequestRoleDialog = () => {
    setIsRequestRoleDialogOpen(false);
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          mt: 10,
        }}
      >
        <Typography
          variant='h4'
          fontWeight={700}
          sx={{
            color: companyTheme ? companyTheme.primary.main : 'text.primary',
          }}
        >
          Role Management
        </Typography>
      </Box>

      {/* Cards Grid */}
      <Grid container spacing={3}>
        {roles.map((role) => (
          <Grid size={{ xs: 12, md: 4,sm: 6,lg:4,xl:3 }} key={role.id}>

            <Card
              sx={{
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                px: 2,
                pt: 2,
                pb: 1,
                minHeight: 210,
                height:"100%",
                cursor: 'pointer',
                background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
                boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                },
              }}
              elevation={2}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    sx={{
                      background: `linear-gradient(135deg, ${
                        companyTheme?.primary?.main || '#2196f3'
                      }, ${alpha(
                        companyTheme?.primary?.main || '#2196f3',
                        0.7,
                      )})`,
                      color: '#fff',
                      fontWeight: 700,
                      width: 46,
                      height: 46,
                      fontSize: 20,
                      boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                    }}
                    alt={role?.alias_name}
                  >
                    {getInitials(role?.alias_name || role?.role_name)}
                  </Avatar>
                  <Box>
                    <Typography
                      variant='h6'
                      fontWeight={600}
                      color='text.primary'
                      sx={{ lineHeight: 1, mb: 0.3 }}
                    >
                      {role?.alias_name || formatRoleName(role?.role_name)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Chip
                  label={`${rolePermissionCounts[role?.id] || 0} permissions`}
                  size='small'
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: '20px',
                    backgroundColor: alpha(
                      companyTheme?.primary?.main || '#2196f3',
                      0.1,
                    ),
                    borderColor: 'transparent',
                    color: companyTheme?.primary?.main || '#2196f3',
                    '&:hover': {
                      backgroundColor: alpha(
                        companyTheme?.primary?.main || '#2196f3',
                        0.2,
                      ),
                    },
                    cursor: 'pointer',
                    margin:2,
                  }}
                  onClick={(e) => handlePermissionChipClick(e, role?.id)}
                />
              <CardContent sx={{ pt: 1, px: 0, pb: 1 }}>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ minHeight: 38 }}
                >
                  {role?.description || <em>No description provided.</em>}
                </Typography>
              </CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1,
                  py: 1,
                  mt: 'auto',
                  borderRadius: 1,
                  minHeight: 44,
                  backgroundColor: 'grey.100',
                }}
              >
                <Chip
                  label={role?.is_active === 1 ? 'Active' : 'Inactive'}
                  variant='outlined'
                  size='small'
                  sx={{
                    fontWeight: 600,
                    px: 1,
                    py: 0.3,
                    color: role?.is_active === 1 ? '#388E3C' : '#c62828',
                    borderColor: role?.is_active === 1 ? '#388E3C' : '#c62828',
                  }}
                />
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    openDialog(role);
                  }}
                  aria-label={`Edit ${role?.role_name}`}
                >
                  <AiOutlineEdit />
                </IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
        <Grid size={{ xs: 12,sm: 6,md: 4,lg: 4,xl: 3}}>

          <Card
            sx={{
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 2,
              minHeight: 210,
              background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
              boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
              },
            }}
            elevation={2}
          >
            {/* Illustration */}
            <Box
              component='img'
              src='/assets/images/request.png'
              alt='Request Role'
              sx={{
                width: '40%',
                maxHeight: 140,
                objectFit: 'contain',
              }}
            />

            {/* Text + Button */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'left',
                ml: 2,
              }}
            >
              <Button
                variant='contained'
                size='small'
                sx={{
                  backgroundColor: companyTheme?.primary?.main || '#7C3AED',
                  color: '#fff',
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  mb: 1,
                  '&:hover': {
                    backgroundColor: companyTheme?.primary?.dark || '#6D28D9',
                  },
                }}
                onClick={() => openRequestRoleDialog()}
              >
                Request Role
              </Button>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ maxWidth: 140, lineHeight: 1.4 }}
              >
                Request new role, <br /> if it doesn't exist.
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog
      slotProps={{
  paper: {
    sx: {
      borderRadius: 3,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    },
  },
}}


        open={isDialogOpen}
        onClose={handleDialogOpen}
        maxWidth='sm'
        fullWidth
      >
        <Formik
          initialValues={{
            role_name: selectedRole?.role_name || '',
            description: selectedRole?.description || '',
            alias_name: selectedRole?.alias_name || '',
            role_menu_flag: selectedRole?.role_menu_flag || '',
          }}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            setSubmitting(true);
            try {
              const updateData = {
                ...values,
                alias_name: values.alias_name === '' ? null : values.alias_name,
              }
              await jwtAxios.put(`/roles/${selectedRole?.id}`, updateData);
              fetchData();
              resetForm();
              closeDialog();
              infoViewActionsContext.showMessage('Role updated successfully!');
            } catch (error) {
              infoViewActionsContext.fetchError(error?.message);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, handleSubmit, setFieldValue }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DialogTitle id='role-dialog-title'>
                  <Typography
                    variant='h4'
                    sx={{
                      color: companyTheme
                        ? companyTheme.primary.main
                        : 'text.primary',
                    }}
                  >
                    Edit Role
                  </Typography>
                </DialogTitle>
                <Box>
                  <IconButton
                    size='small'
                    sx={{ fontWeight: 'bold', color: '#000000' }}
                    onClick={handleDialogOpen}
                  >
                    <CloseRounded />
                  </IconButton>
                </Box>
              </Box>
              <DialogContent>
                <AppGridContainer sx={{ mt: 0.5 }}>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <AppTextField
                      name='role_name'
                      value={values?.role_name}
                      variant='outlined'
                      sx={{ width: '100%' }}
                      label='Role Name'
                      disabled
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <AppTextField
                      name='alias_name'
                      value={values?.alias_name}
                      variant='outlined'
                      sx={{ width: '100%' }}
                      onChange={(e) =>
                        setFieldValue('alias_name', e.target.value)
                      }
                      label='Alias Name'
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <AppTextField
                      label={
                        <Box display='flex' alignItems='center' gap={0.5}>
                          Role Menu Type
                          <Tooltip
                            title='This menu type defines the role type and controls permissions and login access.'
                            arrow
                            placement='right'
                          >
                            <IconButton
                              size='small'
                              sx={{ color: 'text.secondary', p: 0.3 }}
                            >
                              <InfoOutlinedIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      type='text'
                      sx={{ width: '100%' }}
                      variant='outlined'
                      value={values?.role_menu_flag}
                      name='role_menu_flag'
                      disabled
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <AppTextField
                      label='Description'
                      type='text'
                      sx={{ width: '100%' }}
                      variant='outlined'
                      multiline
                      minRows={3}
                      value={values?.description}
                      name='description'
                      disabled
                    />
                  </Grid>
                </AppGridContainer>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleDialogOpen}
                  variant='outlined'
                  sx={{
                    borderColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '&:hover': {
                      borderColor: companyTheme
                        ? alpha(companyTheme.primary.main, 0.8)
                        : alpha('#2196f3', 0.8),
                    },
                    color: companyTheme
                      ? companyTheme.primary.main
                      : 'text.primary',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  sx={{
                    minWidth: 130,
                    backgroundColor: companyTheme?.primary?.main || '#2196f3',
                    '&:hover': {
                      backgroundColor: companyTheme
                        ? alpha(companyTheme?.primary?.main, 0.8)
                        : alpha('#2196f3', 0.8),
                    },
                  }}
                >
                  Update Role
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      {/* Request Role */}
      <Dialog
      slotProps={{
  paper: {
    sx: {
      borderRadius: 3,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    },
  },
}}

        open={isRequestRoleDialogOpen}
        onClose={closeRequestRoleDialog}
        maxWidth='sm'
        fullWidth
      >
        <Formik
          initialValues={{
            roleName: '',
            roleDescription: '',
          }}
          validationSchema={requestRoleValidationSchema}
          enableReinitialize={true}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            setSubmitting(true);
            try {
              await jwtAxios.post('/roles/request-role/customer', values);
              fetchData();
              resetForm();
              closeRequestRoleDialog();
              infoViewActionsContext.showMessage('Email sent successfully!');
            } catch (error) {
              infoViewActionsContext.fetchError(error?.message);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, handleSubmit, setFieldValue }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DialogTitle id='role-dialog-title'>
                  <Box display='flex' alignItems='center' gap={1}>
                    <Typography
                      variant='h4'
                      sx={{
                        color: companyTheme
                          ? companyTheme.primary.main
                          : 'text.primary',
                      }}
                    >
                      Request a New Role
                    </Typography>
                    <Tooltip
                      title='Use this form to request creation of a new role if the one you need doesn’t exist in the system.
                            Your request will be reviewed by the administrator.'
                      arrow
                      placement='right'
                    >
                      <IconButton
                        size='small'
                        sx={{ color: 'text.secondary', p: 0.3 }}
                      >
                        <InfoOutlinedIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </DialogTitle>
                <Box>
                  <IconButton
                    size='small'
                    sx={{ fontWeight: 'bold', color: '#000000' }}
                    onClick={handleDialogOpen}
                  >
                    <CloseRounded />
                  </IconButton>
                </Box>
              </Box>
              <DialogContent>
                <AppGridContainer>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <AppTextField
                      name='roleName'
                      value={values?.roleName}
                      variant='outlined'
                      sx={{ width: '100%' }}
                      label='Role Name'
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <AppTextField
                      label='Description'
                      type='text'
                      sx={{ width: '100%' }}
                      variant='outlined'
                      multiline
                      minRows={3}
                      value={values?.roleDescription}
                      name='roleDescription'
                    />
                  </Grid>
                </AppGridContainer>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleDialogOpen}
                  variant='outlined'
                  sx={{
                    borderColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '&:hover': {
                      borderColor: companyTheme
                        ? alpha(companyTheme.primary.main, 0.8)
                        : alpha('#2196f3', 0.8),
                    },
                    color: companyTheme
                      ? companyTheme.primary.main
                      : 'text.primary',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  sx={{
                    minWidth: 130,
                    backgroundColor: companyTheme?.primary?.main || '#2196f3',
                    '&:hover': {
                      backgroundColor: companyTheme
                        ? alpha(companyTheme?.primary?.main, 0.8)
                        : alpha('#2196f3', 0.8),
                    },
                  }}
                >
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      <AppModalConfirmation
        openDialog={openConfirmationDialog}
        handleCloseDialog={handleCloseDialog}
        handleCloseModalDialog={handleCloseModalDialog}
      />
      <AppInfoView />
    </Box>
  );
}

RoleManagementPage.propTypes = {
  roles: PropTypes.array.isRequired,
  rolePermissionCounts: PropTypes.object.isRequired,
  onPermissionClick: PropTypes.func,
  fetchData: PropTypes.func,
};
